import './QuillEditor.scss';

import * as React from 'react';

import { translate } from '~l10n/client';
import cx from 'classnames';
import Quill, { RangeStatic } from 'quill';
import { useDispatch } from 'react-redux';

import { PaywallScreens } from '~analytics/types';
import { useLink } from '~shared/components/quill/link/useLink';
import TextInput from '~shared/components/TextInput';
import { openPaywall } from '~teacher/slices/teacher/teacherSlice';
import { proCheck } from '~teacher/util/proCheck';

import useEventListener from '../../hooks/useEventListener';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import MathLiveEditor from '../mathlive/MathLiveEditor';
import MathBlot from './blot/MathBlot';
import {
  QuillDebugType,
  QuillTextChangeHandler,
  defaultFormat,
  useQuill,
  useQuillChangeHandler,
  useQuillSelectionChangeHandler,
} from './quillHooks';
import { QuillPopover } from './QuillPopover';
import QuillToolbar from './QuillToolbar';

const Delta = require('quill-delta');

export const editorContext = React.createContext(false);

interface Props {
  initialHtml: string;
  textFieldLabel?: string;
  /* Text to display when the input is empty */
  placeholder?: string;
  /* The maximum amount of text that can be entered */
  maxLength?: number;
  /* auto focus on input */
  autoFocus?: boolean;
  onTextChange?: QuillTextChangeHandler;
  /** Is the field editable? */
  isReadOnly: boolean;
  /* the debug mode of Quill.js */
  debug?: QuillDebugType;
  wrapperStyle?: string;
  onSelectionChanged?: (
    range: RangeStatic,
    oldRange: RangeStatic,
    source: string
  ) => void;
  hasImageField?: boolean;
  /** This is required if `hasImageField` is true. */
  isImageSet?: boolean;
  children?: React.ReactNode;
}

/**
 * Delete existing formulas.
 */
const deleteFormula = ({
  quill,
  range,
}: {
  quill: Quill;
  range: RangeStatic;
}) => {
  const { index } = range;
  const d = new Delta().retain(index).delete(1);
  quill.updateContents(d);
};

/**
 * A mathOp comes from deltas which describe quill content/format. We check the contents of the delta
 * so we can be sure that there is a math formula in the contents of selected text.
 */
const getMathOp = ({
  quill,
  index,
  length,
}: {
  quill: Quill;
  index: number;
  length: number;
}) => {
  const delta = quill.getContents(index, length);
  if (length >= 1 && delta?.ops.length === 1) {
    const { ops } = delta;
    const [mathOp] = ops.filter((op) => !!op?.insert['math']);
    if (mathOp) {
      return mathOp;
    }
  }
  return null;
};

const isFormulaInSelection = (quill: Quill) => {
  // Check if there is a formula the current selection
  if (!quill) {
    return false;
  }
  const range = quill.getSelection();
  if (!range) {
    return false;
  }
  const { index, length } = range;
  const delta = quill.getContents(index, length);
  if (length >= 1 && delta?.ops.length === 1) {
    const { ops } = delta;
    const [mathOp] = ops.filter((op) => !!op?.insert['math']);
    if (mathOp) {
      return true;
    }
  }
};

/**
 * When a formula does not exist/we are inserting a formula anywhere.
 */
const insertFormula = ({
  quill,
  range,
  latex,
}: {
  quill: Quill;
  range: RangeStatic;
  latex: string;
}) => {
  const index = range?.index ?? 0;
  quill.insertEmbed(index, 'math', latex);
  quill.setSelection(index + 1, 0, 'user');
};

/**
 *  When a formula already exists, we need:
 *  Retain all items before the formula
 *  Delete the formula
 *  Insert a new formula at the original index.
 */
const replaceFormula = ({
  quill,
  range,
  latex,
}: {
  quill: Quill;
  range: RangeStatic;
  latex: string;
}) => {
  const index = range?.index ?? 0;
  const d = new Delta().retain(index).delete(1).insert({ math: latex });
  quill.updateContents(d);
};

/**
 * If a formula is selected, we need to switch into replace mode.
 * In replace mode we call replace Formula. in non replacemode we just insert the formula.
 */
const insertOrReplaceFormula = ({
  quill,
  range,
  latex,
  hasExistingValue,
}: {
  quill: Quill;
  range: RangeStatic;
  latex: string;
  hasExistingValue: boolean;
}) => {
  // If there is no latex to insert/replace don't do anything.
  // Otherwise the word "math" will be inserted.
  if (latex?.length >= 1) {
    hasExistingValue
      ? replaceFormula({ quill, range, latex })
      : insertFormula({ quill, range, latex });
  }
};

const createHandleMathButtonPress =
  ({
    showAdvancedQuizEditor,
    dispatch,
    quill,
    setPopOverRange,
    setIsPopoverOpen,
    setHasExistingValue,
    setLatex,
  }: {
    showAdvancedQuizEditor: boolean;
    dispatch: any;
    quill: Quill;
    setPopOverRange: (value: RangeStatic) => void;
    setIsPopoverOpen: (value: boolean) => void;
    setHasExistingValue: (value: boolean) => void;
    setLatex: (value: string) => void;
  }) =>
  () => {
    if (!showAdvancedQuizEditor) {
      dispatch(
        openPaywall({ paywallTouch: [PaywallScreens.QuizEditAddMathEquation] })
      );
      return;
    }

    // When the quill editor loses focus, we lose the current selection.
    // store the current selection, so we can insert the formula at the
    // correct spot.
    let existingLatex;

    const range = quill.getSelection();
    if (range) {
      const { index, length } = range;
      const mathOp = getMathOp({ quill, index, length });
      if (mathOp) {
        existingLatex = mathOp.insert['math'];
      }
    }

    // !! Important If we found existing latex, we need to be in replace mode.
    // Otherwise, the formula cannot be edited.
    if (existingLatex) {
      setHasExistingValue(true);
      setLatex(existingLatex);
    } else {
      setHasExistingValue(false);
    }

    setPopOverRange(range);
    setIsPopoverOpen(true);
  };

const QuillEditor = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      initialHtml,
      textFieldLabel,
      placeholder,
      maxLength,
      autoFocus,
      onTextChange,
      isReadOnly,
      debug,
      wrapperStyle,
      onSelectionChanged = () => {},
      hasImageField,
      isImageSet,
      children,
    }: Props,
    ref
  ) => {
    const { Provider } = editorContext;
    const dispatch = useDispatch();
    const showAdvancedQuizEditor = proCheck.canShowAdvancedQuizEditor();
    const internalEditorRef = React.useRef<HTMLDivElement>(null);
    const scrollContainerRef = React.useRef<HTMLDivElement>();
    const toolbarRef = React.useRef<HTMLDivElement>();

    const [isErrorState, setIsErrorState] = React.useState(false);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [latex, setLatex] = React.useState<string>(null);
    const [popOverRange, setPopOverRange] = React.useState<any>(null);
    const [hasExistingValue, setHasExistingValue] =
      React.useState<boolean>(false);

    // Sync the internal ref with the forwarded ref
    React.useEffect(() => {
      if (typeof ref === 'function') {
        ref(internalEditorRef.current);
      } else if (ref) {
        ref.current = internalEditorRef.current;
      }
    }, [ref]);

    const { setSelection, selection, setContents, quill } = useQuill({
      placeholder,
      isReadOnly,
      editorRef: internalEditorRef,
      scrollContainerRef,
      initialHtml,
      formats: defaultFormat,
      toolbar: toolbarRef,
      debug,
    });

    const hasFocus = quill?.hasFocus();

    /**
     * Disable dragging content into the editor. Setting `dropEffect` to none
     * on `dragover` ensures that the cursor doesn't incorrectly indicate that a
     * drag and drop is possible.
     */
    const dropHandler = (event: DragEvent | React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'none';

      return false;
    };

    useEventListener('dragover', dropHandler, scrollContainerRef.current);
    useEventListener('drop', dropHandler, scrollContainerRef.current);

    useQuillChangeHandler({
      maxLength,
      quill,
      onQuillTextChange: onTextChange,
      setContents,
      setSelection,
    });

    React.useEffect(() => {
      if (autoFocus) {
        quill?.focus();
      }
    }, [autoFocus, quill]);

    React.useEffect(() => {
      if (quill) {
        isReadOnly ? quill.disable() : quill.enable();
      }
    }, [quill, isReadOnly]);

    const isEmpty = quill?.getText().length <= 0;

    useQuillSelectionChangeHandler({ onSelectionChanged, quill, setSelection });

    const onSaveValue = () => {
      insertOrReplaceFormula({
        quill,
        latex,
        hasExistingValue,
        range: popOverRange,
      });
    };

    const handleMathButtonPress = createHandleMathButtonPress({
      showAdvancedQuizEditor,
      dispatch,
      quill,
      setIsPopoverOpen,
      setLatex,
      setPopOverRange,
      setHasExistingValue,
    });

    const handleMathLiveEditorChange = (newLatex: string) => {
      setLatex(newLatex);
    };

    const handleOnCloseQuillPopover = () => {
      setIsPopoverOpen(false);
      setLatex(null);
      setHasExistingValue(false);
      setPopOverRange(null);
    };

    const fieldClass = React.useMemo(
      () =>
        cx('relative flex-1 max-w-full min-w-0', 'quill-field', {
          'is-readonly': isReadOnly,
          'has-image-field': hasImageField,
          'is-image-set': isImageSet,
        }),
      [isReadOnly, hasImageField, isImageSet]
    );
    const wrapperClass = React.useMemo(
      () =>
        cx(
          'relative z-10 flex-1 max-w-full bg-white rounded-2',
          'quill-wrapper',
          {
            'flex flex-wrap lg:flex-no-wrap': !isReadOnly,
            'shadow-focus': hasFocus,
            [wrapperStyle as string]: !!wrapperStyle,
          }
        ),
      [isReadOnly, hasFocus, wrapperStyle]
    );
    const inputClass = React.useMemo(
      () =>
        cx(
          'notranslate' /** workaround: to prevent quill editor from not working when google translate is used */,
          'flex flex-grow-1 h-auto py-2 font-sans text-blue-900 text-base leading-8 border-0 rounded-2',
          {
            'has-focus': hasFocus,
            'max-h-full min-h-full': isReadOnly,
            'overflow-hidden': isEmpty,
            'overflow-auto': !isEmpty,
            'w-full min-w-50 min-h-12 max-h-80 px-4': !isReadOnly,
            'sr-only': isReadOnly && isEmpty,
          }
        ),
      [hasFocus, isEmpty, isReadOnly]
    );

    // If you want to trigger events when elements inside of the quill editor
    // are clicked, use this click handler!
    // Example: When math blots are clicked we want to make sure that selection expands
    // to select the entire blot.
    const onQuillEditorClickCapture: React.MouseEventHandler<HTMLDivElement> = (
      event
    ) => {
      try {
        const mathNode = (event.target as Element).closest(
          `.${MathBlot.className}`
        );
        if (mathNode) {
          const mathBlot = Quill.find(mathNode);
          const index = quill.getIndex(mathBlot);
          quill.setSelection(index, 1, 'user');
        }
      } catch (e) {
        console.log(
          `Error encountered while attempting to select element: ${e}`
        );
      }
      event.stopPropagation();
    };

    const onRemoveValue = () => {
      deleteFormula({ quill, range: popOverRange });
    };

    const isMathFormulaSelected = isFormulaInSelection(quill);
    const isSaveLatexDisabled = !(latex?.length > 0);

    const {
      showLinkModal,
      handleLinkModalClose,
      handleLinkButtonClick,
      linkPlaceholder,
      onLinkChange,
      currentLink,
      onLinkSave,
      onLinkDelete,
      isLinkSaveDisabled,
      label,
      onLabelChange,
      originalLink,
    } = useLink({ quill, selection });

    return (
      <Provider value={isEmpty}>
        <div className={fieldClass}>
          <div className={wrapperClass}>
            <div className="flex-1 max-w-full quill-editor">
              <QuillToolbar
                ref={toolbarRef}
                isLinkSelected={!!originalLink}
                onLinkButtonPress={handleLinkButtonClick}
                onMathButtonPress={handleMathButtonPress}
                isVisible={selection !== null && !isReadOnly}
                isMathFormulaSelected={isMathFormulaSelected}
              />
              <div
                /** workaround: to prevent quill editor from not working when google translate is used */
                translate="no"
                className={inputClass}
                ref={scrollContainerRef}
                aria-label={textFieldLabel}
              >
                <div
                  onClickCapture={onQuillEditorClickCapture}
                  className="relative flex flex-grow h-full min-h-full"
                  data-testid="quill-editor"
                  role="textbox"
                  aria-multiline="true"
                  ref={internalEditorRef}
                />
              </div>
            </div>
            {children}
            <QuillPopover
              title={
                originalLink ? translate('Edit Link') : translate('Insert Link')
              }
              open={showLinkModal}
              onClose={handleLinkModalClose}
              onSaveValue={onLinkSave}
              hasExistingValue={!!currentLink}
              onRemoveValue={onLinkDelete}
              isSaveDisabled={isLinkSaveDisabled}
              isErrorState={false}
            >
              <LinkPopoverContent
                linkOnChange={onLinkChange}
                linkPlaceholder={linkPlaceholder}
                newLink={currentLink}
                label={label}
                onLabelChange={onLabelChange}
              />
            </QuillPopover>
            <QuillPopover
              title={
                hasExistingValue
                  ? translate('Edit Equation')
                  : translate('Insert Equation')
              }
              open={isPopoverOpen}
              onClose={handleOnCloseQuillPopover}
              onSaveValue={onSaveValue}
              hasExistingValue={hasExistingValue}
              onRemoveValue={onRemoveValue}
              isSaveDisabled={isSaveLatexDisabled}
              isErrorState={isErrorState}
            >
              <ErrorBoundary
                extraErrorInfo={'Latex Used: ' + latex}
                handleTryAgain={() => {
                  setIsErrorState(false);
                }}
                handleErrorCallback={() => {
                  setIsErrorState(true);
                }}
              >
                <MathLiveEditor
                  latex={latex}
                  onChange={handleMathLiveEditorChange}
                />
              </ErrorBoundary>
            </QuillPopover>
          </div>
        </div>
      </Provider>
    );
  }
);

QuillEditor.displayName = 'QuillEditor';

export default QuillEditor;

// link popover content
const LinkPopoverContent = ({
  linkPlaceholder,
  newLink,
  linkOnChange,
  label,
  onLabelChange,
}: {
  linkPlaceholder: string;
  newLink: string;
  linkOnChange: React.ChangeEventHandler<HTMLInputElement>;
  label: string;
  onLabelChange: React.ChangeEventHandler<HTMLInputElement>;
}) => {
  const [refState, setRefState] = React.useState<HTMLInputElement | null>(null);
  React.useEffect(() => {
    if (!refState) return;
    refState.focus();
  }, [refState]);
  return (
    <>
      <label className={'text-base leading-8'}>
        {translate('Link label')}
        <TextInput
          placeholder={'the selected text'}
          value={label}
          onChange={onLabelChange}
          className={'mb-6 mt-3'}
        />
      </label>
      <label className={'text-base leading-8'}>
        {translate('Link')}
        <TextInput
          ref={(ref) => setRefState(ref)}
          placeholder={linkPlaceholder}
          value={newLink}
          onChange={linkOnChange}
          className={'mt-3'}
        />
      </label>
    </>
  );
};
